import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from 'remotion';

// #1 逐字显示效果 (Typewriter)
export const TypewriterSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
}> = ({ text, fontSize = 60, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  const chars = text.split('');
  const charsToShow = Math.floor(interpolate(frame, [0, text.length * 3], [0, chars.length], {
    extrapolateRight: 'clamp',
  }));

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      }}
    >
      {chars.slice(0, charsToShow).join('')}
      {charsToShow < chars.length && (
        <span style={{ opacity: frame % 10 < 5 ? 1 : 0 }}>|</span>
      )}
    </AbsoluteFill>
  );
};

// #2 弹跳文字效果 (Bounce)
export const BounceSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
}> = ({ text, fontSize = 60, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const chars = text.split('');

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        display: 'flex',
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      }}
    >
      {chars.map((char, i) => {
        const delay = i * 2;
        const bounce = spring({
          frame: frame - delay,
          fps,
          config: {
            damping: 10,
            stiffness: 100,
            mass: 0.5,
          },
        });
        const y = interpolate(bounce, [0, 1], [-50, 0]);
        
        return (
          <span
            key={i}
            style={{
              display: 'inline-block',
              transform: `translateY(${y}px)`,
            }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        );
      })}
    </AbsoluteFill>
  );
};

// #3 渐变字幕效果 (Gradient)
export const GradientSubtitle: React.FC<{
  text: string;
  fontSize?: number;
}> = ({ text, fontSize = 60 }) => {
  const frame = useCurrentFrame();
  const gradientAngle = interpolate(frame, [0, 100], [0, 360]);

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        fontWeight: 'bold',
        background: `linear-gradient(${gradientAngle}deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textShadow: 'none',
      }}
    >
      {text}
    </AbsoluteFill>
  );
};

// #4 模糊淡入效果 (Blur Fade)
export const BlurFadeSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
}> = ({ text, fontSize = 60, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const opacity = interpolate(frame, [0, 20], [0, 1], {
    extrapolateRight: 'clamp',
  });
  
  const blur = interpolate(frame, [0, 20], [20, 0], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        opacity,
        filter: `blur(${blur}px)`,
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      }}
    >
      {text}
    </AbsoluteFill>
  );
};

// #5 波浪字幕效果 (Wave)
export const WaveSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
}> = ({ text, fontSize = 60, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  const chars = text.split('');

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        display: 'flex',
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      }}
    >
      {chars.map((char, i) => {
        const y = Math.sin((frame + i * 10) * 0.1) * 20;
        
        return (
          <span
            key={i}
            style={{
              display: 'inline-block',
              transform: `translateY(${y}px)`,
            }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        );
      })}
    </AbsoluteFill>
  );
};

// #6 描边字幕效果 (Outline)
export const OutlineSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
  outlineColor?: string;
}> = ({ text, fontSize = 60, color = '#ffffff', outlineColor = '#000000' }) => {
  const frame = useCurrentFrame();
  const outlineWidth = interpolate(frame, [0, 30], [0, 4], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        WebkitTextStroke: `${outlineWidth}px ${outlineColor}`,
        paintOrder: 'stroke fill',
      }}
    >
      {text}
    </AbsoluteFill>
  );
};

// #7 发光字幕效果 (Glow)
export const GlowSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
  glowColor?: string;
}> = ({ text, fontSize = 60, color = '#ffffff', glowColor = '#00ffff' }) => {
  const frame = useCurrentFrame();
  const glowIntensity = interpolate(
    Math.sin(frame * 0.1),
    [-1, 1],
    [10, 30]
  );

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        textShadow: `0 0 ${glowIntensity}px ${glowColor}, 0 0 ${glowIntensity * 2}px ${glowColor}`,
      }}
    >
      {text}
    </AbsoluteFill>
  );
};

// #8 旋转字幕效果 (Rotate)
export const RotateSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
}> = ({ text, fontSize = 60, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const rotation = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
    },
  });
  
  const angle = interpolate(rotation, [0, 1], [-180, 0]);

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        transform: `rotate(${angle}deg)`,
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      }}
    >
      {text}
    </AbsoluteFill>
  );
};

// #9 缩放弹入效果 (Scale In)
export const ScaleInSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
}> = ({ text, fontSize = 60, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const scale = spring({
    frame,
    fps,
    config: {
      damping: 12,
      stiffness: 200,
    },
  });

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        transform: `scale(${scale})`,
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      }}
    >
      {text}
    </AbsoluteFill>
  );
};

// #10 3D 翻转效果 (3D Flip)
export const Flip3DSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
}> = ({ text, fontSize = 60, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const progress = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
    },
  });
  
  const rotateY = interpolate(progress, [0, 1], [90, 0]);

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        transform: `perspective(1000px) rotateY(${rotateY}deg)`,
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      }}
    >
      {text}
    </AbsoluteFill>
  );
};

// #11 打字机 + 删除效果 (Type & Delete)
export const TypeDeleteSubtitle: React.FC<{
  texts: string[];
  fontSize?: number;
  color?: string;
}> = ({ texts, fontSize = 60, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  const cycleDuration = 60; // 2 seconds per text
  const currentCycle = Math.floor(frame / cycleDuration) % texts.length;
  const currentText = texts[currentCycle];
  const cycleFrame = frame % cycleDuration;
  
  const chars = currentText.split('');
  const halfCycle = cycleDuration / 2;
  
  let charsToShow: number;
  if (cycleFrame < halfCycle) {
    // Typing
    charsToShow = Math.floor(interpolate(cycleFrame, [0, halfCycle], [0, chars.length], {
      extrapolateRight: 'clamp',
    }));
  } else {
    // Deleting
    charsToShow = Math.floor(interpolate(cycleFrame, [halfCycle, cycleDuration], [chars.length, 0], {
      extrapolateRight: 'clamp',
    }));
  }

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      }}
    >
      {chars.slice(0, charsToShow).join('')}
    </AbsoluteFill>
  );
};

// #12 多行滚动字幕 (Scrolling Lines)
export const ScrollingLinesSubtitle: React.FC<{
  lines: string[];
  fontSize?: number;
  color?: string;
}> = ({ lines, fontSize = 48, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  const lineDuration = 40; // 1.33 seconds per line
  const currentLine = Math.floor(frame / lineDuration) % lines.length;
  const lineFrame = frame % lineDuration;
  
  const { fps } = useVideoConfig();
  
  const enterExit = spring({
    frame: lineFrame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
    },
  });
  
  const y = interpolate(enterExit, [0, 1], [50, 0]);
  const opacity = interpolate(enterExit, [0, 1], [0, 1]);

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        color,
        fontWeight: 'bold',
        transform: `translateY(${y}px)`,
        opacity,
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
      }}
    >
      {lines[currentLine]}
    </AbsoluteFill>
  );
};

// #13 彩虹渐变字幕 (Rainbow)
export const RainbowSubtitle: React.FC<{
  text: string;
  fontSize?: number;
}> = ({ text, fontSize = 60 }) => {
  const frame = useCurrentFrame();
  const chars = text.split('');

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        fontWeight: 'bold',
        display: 'flex',
      }}
    >
      {chars.map((char, i) => {
        const hue = (frame * 2 + i * 30) % 360;
        
        return (
          <span
            key={i}
            style={{
              display: 'inline-block',
              color: `hsl(${hue}, 80%, 60%)`,
              textShadow: `2px 2px 4px hsla(${hue}, 80%, 30%, 0.8)`,
            }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        );
      })}
    </AbsoluteFill>
  );
};

// #14 故障文字效果 (Glitch)
export const GlitchSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  color?: string;
}> = ({ text, fontSize = 60, color = '#ffffff' }) => {
  const frame = useCurrentFrame();
  
  const glitch1 = Math.sin(frame * 0.5) * 5;
  const glitch2 = Math.cos(frame * 0.7) * 3;
  
  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        fontWeight: 'bold',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          color: '#ff0000',
          transform: `translate(${glitch1}px, 0)`,
          opacity: 0.7,
        }}
      >
        {text}
      </div>
      <div
        style={{
          position: 'absolute',
          color: '#00ff00',
          transform: `translate(${glitch2}px, 0)`,
          opacity: 0.7,
        }}
      >
        {text}
      </div>
      <div style={{ color }}>{text}</div>
    </AbsoluteFill>
  );
};

// #15 卡拉OK 风格字幕 (Karaoke)
export const KaraokeSubtitle: React.FC<{
  text: string;
  fontSize?: number;
  duration: number; // in frames
}> = ({ text, fontSize = 60, duration }) => {
  const frame = useCurrentFrame();
  const progress = Math.min(frame / duration, 1);
  const chars = text.split('');
  const filledChars = Math.floor(progress * chars.length);

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        fontSize,
        fontWeight: 'bold',
        display: 'flex',
      }}
    >
      {chars.map((char, i) => {
        const isFilled = i < filledChars;
        
        return (
          <span
            key={i}
            style={{
              display: 'inline-block',
              color: isFilled ? '#ffd700' : '#888888',
              textShadow: isFilled
                ? '0 0 10px #ffd700'
                : '2px 2px 4px rgba(0,0,0,0.8)',
              transition: 'color 0.1s',
            }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        );
      })}
    </AbsoluteFill>
  );
};
